import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"
import { addMonths, endOfDay, format, startOfDay } from "date-fns"
import { AI_BASE_URL, aiApiKeyHeaders } from "../../lib/aiApiBaseUrl"
import { get, post } from "../../lib/request"
import {
  mapPipelineTopicsToPlannerGroups,
  mergePipelineTopicsIntoSavePayload,
  stripPipelineSyncedPostsFromPersistedSavePayload,
} from "../../lib/aiPlannerMappers"
import { saveAIPlannerSession, completeGeneration } from "./aiPlannerSlice"
import { AI_PLANNER_READY_TOAST, showAppToast } from "../../lib/showAppToast"

/**
 * Base URL for AI Planner service (event-media preload, captions, regenerate, generate chain).
 * Uses the same host as client media and other AI APIs — see {@link AI_BASE_URL} in lib/aiApiBaseUrl.js
 * (REACT_APP_AI_API_BASE_URL). Optional override: REACT_APP_AI_BASE_URL or REACT_APP_AI_PLANNER_BASE_URL.
 * No trailing slash.
 */
export const getAiPlannerApiBaseUrl = () => {
  const explicit = (
    process.env.REACT_APP_AI_BASE_URL ||
    process.env.REACT_APP_AI_PLANNER_BASE_URL ||
    ""
  )
    .trim()
    .replace(/\/$/, "")
  if (explicit) return explicit
  const shared = String(AI_BASE_URL || "")
    .trim()
    .replace(/\/$/, "")
  return shared
}

function getBearerToken(getState) {
  const state = getState?.() || {}
  const auth = state.auth || {}
  return (
    auth.token ||
    auth.accessToken ||
    auth.user?.token ||
    (typeof localStorage !== "undefined" ? localStorage.getItem("token") : null)
  )
}

async function postAiPlannerJson(path, body, getState) {
  const base = getAiPlannerApiBaseUrl()
  if (!base) {
    throw new Error("AI service URL is not configured. Set REACT_APP_AI_API_BASE_URL.")
  }
  const url = `${base}${path.startsWith("/") ? path : `/${path}`}`
  const token = getBearerToken(getState)
  if (process.env.NODE_ENV === "development") {
    // eslint-disable-next-line no-console
    console.debug("[AI Planner] POST", url)
  }
  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...aiApiKeyHeaders(),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(body),
  })
  const text = await response.text()
  let data = null
  if (text?.trim()) {
    try {
      data = JSON.parse(text)
    } catch {
      data = { ok: false, error: text || "Invalid response" }
    }
  }
  if (!response.ok) {
    const msg = data?.error || data?.message || `Request failed (${response.status})`
    throw new Error(msg)
  }
  if (data && data.ok === false) {
    throw new Error(data.error || "AI Planner request failed")
  }
  return data
}

/** Poll GET topics (status 0) every 20s until merged rows appear or max attempts. */
const AI_PLANNER_PIPELINE_POLL_MS = 20_000
const AI_PLANNER_PIPELINE_POLL_MAX = 90

/** Local-calendar GET `topics/…` window: today through end of day three months later. */
export function getRollingThreeMonthTopicsQueryRange() {
  const start = startOfDay(new Date())
  const end = endOfDay(addMonths(start, 3))
  return {
    startDate: format(start, "yyyy-MM-dd"),
    endDate: format(end, "yyyy-MM-dd"),
  }
}

/** Default planning window for session payloads (matches AI Planner UI: one local calendar month). */
function getDefaultOneMonthIsoDateRange() {
  const start = startOfDay(new Date())
  const end = endOfDay(addMonths(start, 1))
  return [
    new Date(`${format(start, "yyyy-MM-dd")}T00:00:00.000Z`).toISOString(),
    new Date(`${format(end, "yyyy-MM-dd")}T23:59:59.999Z`).toISOString(),
  ]
}

/** Session `dateRange` for merge save: never use the wide GET window; use persisted planner range (any length) or default. */
function mergeSessionDateRangeForPipeline(persisted, clientId) {
  if (persisted?.dateRange?.length === 2 && persisted.clientId === clientId) {
    const d0 = startOfDay(new Date(persisted.dateRange[0]))
    let d1End = endOfDay(startOfDay(new Date(persisted.dateRange[1])))
    if (startOfDay(d1End) < d0) d1End = endOfDay(d0)
    return [
      new Date(`${format(d0, "yyyy-MM-dd")}T00:00:00.000Z`).toISOString(),
      new Date(`${format(d1End, "yyyy-MM-dd")}T23:59:59.999Z`).toISOString(),
    ]
  }
  return getDefaultOneMonthIsoDateRange()
}

/** True if mapper output includes at least one AI pipeline post still in status 0 (not draft/scheduled only). */
function pipelineGroupsIncludePendingStatusZero(pipelineGroups) {
  if (!Array.isArray(pipelineGroups) || pipelineGroups.length === 0) return false
  return pipelineGroups.some((g) =>
    (g.posts || []).some((p) => {
      const sid = Number(p?.contentPostStatusId ?? p?.contentPostStatus?.id)
      return sid === 0
    }),
  )
}

async function pollPipelinePostsUntilReady(dispatch, opts) {
  const { clientId, replacePipelinePostsInMatchedGroups, postStatusIds, onPollAttempt } = opts
  for (let attempt = 0; attempt < AI_PLANNER_PIPELINE_POLL_MAX; attempt += 1) {
    onPollAttempt?.(attempt)
    try {
      const r = await dispatch(
        fetchPipelinePostsForAiPlanner({
          clientId,
          replacePipelinePostsInMatchedGroups,
          /** While waiting for new AI rows, only query status 0 so old draft/scheduled topics do not satisfy the poll early. */
          postStatusIds: postStatusIds ?? [0],
        }),
      ).unwrap()
      const merged = Number(r?.merged) || 0
      const hasPending = Boolean(r?.hasPipelinePendingRows)
      /* After start over, old topics may still return draft/scheduled only — merged > 0 but no status 0 yet; keep polling for the new topic's pipeline rows. */
      if (r && merged > 0 && hasPending) return { ...r, timedOut: false }
    } catch {
      /* keep polling — drafts may not exist yet */
    }
    if (attempt < AI_PLANNER_PIPELINE_POLL_MAX - 1) {
      await new Promise((resolve) => setTimeout(resolve, AI_PLANNER_PIPELINE_POLL_MS))
    }
  }
  return { merged: 0, timedOut: true, hasPipelinePendingRows: false }
}

/**
 * Stable fingerprint for an `/api/event-media` POST body (used after `postEventMedia` and for session metadata).
 */
export function fingerprintEventMediaPayload(payload) {
  if (!payload || typeof payload !== "object") return ""
  try {
    const clientId = String(payload.clientId ?? "")
    const threshold = payload.threshold ?? ""
    const dateRange =
      payload.dateRange && typeof payload.dateRange === "object"
        ? `${String(payload.dateRange.start ?? "")}|${String(payload.dateRange.end ?? "")}`
        : ""
    const events = Array.isArray(payload.events) ? payload.events : []
    const normEvents = events
      .map((e) => ({
        id: String(e?.id ?? ""),
        name: String(e?.name ?? ""),
        type: String(e?.type ?? ""),
        date: String(e?.date ?? ""),
        description: String(e?.description ?? ""),
      }))
      .sort((a, b) => a.id.localeCompare(b.id))
    const cadence = Array.isArray(payload.cadence) ? payload.cadence : []
    const cadenceKey = cadence
      .map((c) => JSON.stringify(c))
      .sort()
      .join("\u0001")
    const mediaAssetIds = Array.isArray(payload.mediaAssetIds) ? payload.mediaAssetIds : []
    const normMedia = mediaAssetIds
      .map((id) => Number(id))
      .filter((id) => !Number.isNaN(id))
      .sort((a, b) => a - b)
    return JSON.stringify({
      clientId,
      threshold,
      dateRange,
      events: normEvents,
      cadenceKey,
      mediaAssetIds: normMedia,
    })
  } catch {
    return ""
  }
}

function sortedNormMediaIdsFromPayload(payload) {
  const mediaAssetIds = Array.isArray(payload?.mediaAssetIds) ? payload.mediaAssetIds : []
  return mediaAssetIds
    .map((id) => Number(id))
    .filter((id) => !Number.isNaN(id))
    .sort((a, b) => a - b)
}

/**
 * Step 4: find relevant media + map to events
 */
export const postEventMedia = createAsyncThunk(
  "aiPlannerApi/postEventMedia",
  async (payload, { rejectWithValue, getState }) => {
    try {
      return await postAiPlannerJson("/api/event-media", payload, getState)
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to load event media")
    }
  },
)

/** Calendar create-post: match media to specific event(s) by asset ids + pillars. */
export const postEventMediaByIds = createAsyncThunk(
  "aiPlannerApi/postEventMediaByIds",
  async (payload, { rejectWithValue, getState }) => {
    try {
      return await postAiPlannerJson("/api/event-media/by-ids", payload, getState)
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to load event media")
    }
  },
)

/**
 * Step 5: generate captions / posts
 */
export const postAiCaptions = createAsyncThunk(
  "aiPlannerApi/postAiCaptions",
  async (payload, { rejectWithValue, getState }) => {
    try {
      return await postAiPlannerJson("/api/ai/captions", payload, getState)
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to generate captions")
    }
  },
)

/**
 * Step 6: regenerate captions only (same images)
 */
export const postRegenerateAiPosts = createAsyncThunk(
  "aiPlannerApi/postRegenerateAiPosts",
  async (payload, { rejectWithValue, getState }) => {
    try {
      return await postAiPlannerJson("/api/regenerate-posts", payload, getState)
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to regenerate posts")
    }
  },
)

/**
 * GET topics for AI Planner pipeline merge.
 * Query always uses a rolling three-calendar-month window from today (local date) through end of day three months later;
 * any `startDate` / `endDate` on the payload are ignored.
 * Default `post_status_ids`: 0, 1, 6. Pass `postStatusIds: [0]` during post-generate polling so only new pipeline rows drive readiness.
 */
export const fetchPipelinePostsForAiPlanner = createAsyncThunk(
  "aiPlannerApi/fetchPipelinePosts",
  async (
    { clientId, replacePipelinePostsInMatchedGroups, postStatusIds },
    { dispatch, getState, rejectWithValue },
  ) => {
    try {
      if (!clientId) {
        return rejectWithValue("Missing client id")
      }
      const { startDate, endDate } = getRollingThreeMonthTopicsQueryRange()
      const qs = new URLSearchParams({ start_date: startDate, end_date: endDate })
      const statuses =
        Array.isArray(postStatusIds) && postStatusIds.length > 0 ? postStatusIds : [0, 1, 6]
      statuses.forEach((sid) => qs.append("post_status_ids", String(sid)))
      const data = await get(`topics/clients/${clientId}?${qs.toString()}`)
      const list = Array.isArray(data) ? data : []
      const isStatusZeroOnlyFetch = statuses.length === 1 && Number(statuses[0]) === 0
      const socialNetworkTypes = getState().socialNetworkTypes?.types ?? []
      const pipelineGroups = mapPipelineTopicsToPlannerGroups(list, {
        allowedPostStatusIds: statuses,
        socialNetworkTypes,
      })
      if (process.env.NODE_ENV === "development") {
        // eslint-disable-next-line no-console
        console.debug("[AI Planner][fetchPipelinePosts] response", {
          clientId,
          statuses,
          apiTopics: list.length,
          mappedGroups: pipelineGroups.length,
          mappedPosts: pipelineGroups.reduce((sum, g) => sum + (g?.posts?.length || 0), 0),
        })
      }
      const state = getState()
      const persisted = state.aiPlanner?.persistedSession
      const mergeDateRange = mergeSessionDateRangeForPipeline(persisted, clientId)
      const hasPersistedSessionGroups =
        persisted?.clientId === clientId &&
        Array.isArray(persisted?.generatedEventGroups) &&
        persisted.generatedEventGroups.length > 0
      const hasPipelinePendingRows = pipelineGroupsIncludePendingStatusZero(pipelineGroups)

      if (pipelineGroups.length === 0) {
        if (list.length === 0) {
          const clearPayload = stripPipelineSyncedPostsFromPersistedSavePayload(persisted, clientId, mergeDateRange)
          if (clearPayload) {
            dispatch(saveAIPlannerSession(clearPayload))
          }
        }
        return { merged: 0, hasPipelinePendingRows: false }
      }
      const replaceMerged =
        replacePipelinePostsInMatchedGroups !== false
      /** Full 0+1+6 refetch: only merge draft/scheduled for posts already in this session (see mapper). */
      const restrictNonZeroToKnownServerIds = !isStatusZeroOnlyFetch && hasPersistedSessionGroups
      const mergePayloadOpts = {
        clientId,
        replacePipelinePostsInMatchedGroups: replaceMerged,
        pruneStalePipelineGroups: isStatusZeroOnlyFetch && replaceMerged,
        restrictNonZeroToKnownServerIds,
      }
      mergePayloadOpts.dateRange = mergeDateRange
      const payload = mergePipelineTopicsIntoSavePayload(persisted, pipelineGroups, mergePayloadOpts)
      if (payload) {
        dispatch(saveAIPlannerSession(payload))
      }
      return { merged: pipelineGroups.length, hasPipelinePendingRows }
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to sync pipeline posts")
    }
  },
)

/** True if the browser URL looks like the AI Planner screen (pathname or hash routes). */
function isCurrentlyOnAiPlannerRoute() {
  if (typeof window === "undefined") return false
  const path = (window.location.pathname || "").toLowerCase()
  const hash = (window.location.hash || "").toLowerCase()
  return `${path}${hash}`.includes("ai-planner")
}

/**
 * Build `eventMediaMap` for `posts/ai-planner` from the Add Media grid only (no `/api/event-media` call).
 * Each selected event receives the same sorted list of `mediaAssetIds` so generation uses exactly what the user kept in the UI.
 */
export function buildEventMediaMapFromGridSelection(events, mediaAssetIds) {
  const ids = sortedNormMediaIdsFromPayload({ mediaAssetIds })
  const out = {}
  if (Array.isArray(events)) {
    events.forEach((ev) => {
      if (ev && ev.id != null && String(ev.id) !== "") {
        out[String(ev.id)] = [...ids]
      }
    })
  }
  return out
}

function normMediaAssetId(raw) {
  if (raw == null || raw === "") return null
  const n = typeof raw === "number" ? raw : Number(raw)
  return Number.isNaN(n) ? null : n
}

function mediaAssetIdsFromEventMediaBucket(arr) {
  if (!Array.isArray(arr)) return []
  const out = []
  const seen = new Set()
  for (const item of arr) {
    let id = null
    if (typeof item === "number") {
      id = normMediaAssetId(item)
    } else if (item && typeof item === "object") {
      id = normMediaAssetId(
        item.media_asset_id ?? item.mediaAssetId ?? item.media_id ?? item.mediaId ?? item.id,
      )
    }
    if (id == null || seen.has(id)) continue
    seen.add(id)
    out.push(id)
  }
  return out
}

const EVENT_MEDIA_PER_EVENT_LIMIT = 3

function mediaFilenameKey(name) {
  if (name == null) return ""
  const raw = String(name).trim().toLowerCase()
  if (!raw || raw === "media asset") return ""
  return raw.split(/[\\/]/).pop() || ""
}

/** Map dropped duplicate ids (same filename) to the unique asset still on the Add Media grid. */
function buildKeptGridIdAliases(eventMediaResult, gridSet) {
  const alias = new Map()
  if (!eventMediaResult || gridSet.size === 0) return alias

  const rows = []
  if (Array.isArray(eventMediaResult.relevantMedia)) {
    eventMediaResult.relevantMedia.forEach((row) => {
      if (row && typeof row === "object") rows.push(row)
    })
  }
  const map = eventMediaResult.eventMediaMap
  if (map && typeof map === "object") {
    Object.values(map).forEach((bucket) => {
      if (!Array.isArray(bucket)) return
      bucket.forEach((row) => {
        if (row && typeof row === "object") rows.push(row)
      })
    })
  }

  const keptByFilename = new Map()
  rows.forEach((row) => {
    const assetId = normMediaAssetId(row.media_asset_id ?? row.mediaAssetId)
    const mediaId = normMediaAssetId(row.media_id ?? row.mediaId)
    const filename = mediaFilenameKey(row.asset_filename ?? row.system_asset_filename)
    const kept =
      (assetId != null && gridSet.has(assetId) && assetId) ||
      (mediaId != null && gridSet.has(mediaId) && mediaId) ||
      null
    if (kept == null || !filename || keptByFilename.has(filename)) return
    keptByFilename.set(filename, kept)
  })

  rows.forEach((row) => {
    const assetId = normMediaAssetId(row.media_asset_id ?? row.mediaAssetId)
    const mediaId = normMediaAssetId(row.media_id ?? row.mediaId)
    const filename = mediaFilenameKey(row.asset_filename ?? row.system_asset_filename)
    const kept =
      (assetId != null && gridSet.has(assetId) && assetId) ||
      (mediaId != null && gridSet.has(mediaId) && mediaId) ||
      (filename ? keptByFilename.get(filename) : null) ||
      null
    if (kept == null) return
    if (assetId != null) alias.set(String(assetId), kept)
    if (mediaId != null) alias.set(String(mediaId), kept)
  })

  return alias
}

function relevanceScoreForEventAsset(eventMediaResult, eventKey, assetId) {
  const idStr = String(assetId)
  const bucket = eventMediaResult?.eventMediaMap?.[eventKey]
  if (Array.isArray(bucket)) {
    for (const item of bucket) {
      if (!item || typeof item !== "object") continue
      const ids = [item.media_asset_id, item.mediaAssetId, item.media_id, item.mediaId, item.id]
      if (ids.some((id) => id != null && String(id) === idStr)) {
        const score = Number(item.relevance_score ?? item.relevanceScore ?? NaN)
        return Number.isNaN(score) ? 0 : score
      }
    }
  }
  const rows = Array.isArray(eventMediaResult?.relevantMedia) ? eventMediaResult.relevantMedia : []
  for (const item of rows) {
    const ids = [item?.media_asset_id, item?.mediaAssetId, item?.media_id, item?.mediaId]
    if (ids.some((id) => id != null && String(id) === idStr)) {
      const score = Number(item.relevance_score ?? item.relevanceScore ?? NaN)
      return Number.isNaN(score) ? 0 : score
    }
  }
  return 0
}

/**
 * Build per-event `eventMediaMap` for `posts/ai-planner` from POST `/api/event-media` response.
 * Keys match caption `events[].id` (source event id). Values are `media_asset_id` lists per event.
 * When the user removed assets from the grid, each event list is intersected with kept grid ids.
 * Each event is limited to its top 3 remaining unique assets by relevance score.
 */
export function buildEventMediaMapForAiPlannerPayload(eventMediaResult, events, gridMediaAssetIds) {
  const fromApi = buildEventMediaMapIds(eventMediaResult)
  const gridSet = new Set(sortedNormMediaIdsFromPayload({ mediaAssetIds: gridMediaAssetIds }))
  const aliases = buildKeptGridIdAliases(eventMediaResult, gridSet)
  const out = {}

  if (!Array.isArray(events)) return out

  events.forEach((ev) => {
    const eventKey = ev?.id != null ? String(ev.id) : ""
    if (!eventKey) return

    const apiIds = mediaAssetIdsFromEventMediaBucket(fromApi[eventKey])
    if (apiIds.length === 0) {
      out[eventKey] = []
      return
    }

    const resolvedIds = []
    const seen = new Set()
    apiIds.forEach((id) => {
      const resolved =
        gridSet.size === 0 ? id : gridSet.has(id) ? id : aliases.get(String(id))
      if (resolved == null || seen.has(resolved)) return
      if (gridSet.size > 0 && !gridSet.has(resolved)) return
      seen.add(resolved)
      resolvedIds.push(resolved)
    })

    resolvedIds.sort(
      (a, b) =>
        relevanceScoreForEventAsset(eventMediaResult, eventKey, b) -
        relevanceScoreForEventAsset(eventMediaResult, eventKey, a),
    )
    out[eventKey] = resolvedIds.slice(0, EVENT_MEDIA_PER_EVENT_LIMIT)
  })

  return out
}

/**
 * Kicks off AI generation: POST `posts/ai-planner` only. Does not call `/api/event-media` on Generate
 * (`eventMediaPayload` is still used for `mediaAssetIds` + fingerprinting of the session).
 */
export const generateAiPlannerPosts = createAsyncThunk(
  "aiPlannerApi/generateAiPlannerPosts",
  async (
    { eventMediaPayload, captionsPayloadPartial, sessionContext, eventMediaResult },
    { dispatch, rejectWithValue },
  ) => {
    try {
      const appendOnly = sessionContext?.pipelineMergeMode === "append"
      const gridIds = sortedNormMediaIdsFromPayload(eventMediaPayload)
      const hasApiEventMap =
        eventMediaResult &&
        (eventMediaResult.eventMediaMap || eventMediaResult.mappings)
      const eventMediaMapIds = gridIds.length > 0
        ? buildEventMediaMapFromGridSelection(captionsPayloadPartial?.events, gridIds)
        : hasApiEventMap
          ? buildEventMediaMapForAiPlannerPayload(
              eventMediaResult,
              captionsPayloadPartial?.events,
              gridIds,
            )
          : {}
      const captionsPayload = {
        ...captionsPayloadPartial,
        eventMediaMap: eventMediaMapIds,
      }

      // POST posts/ai-planner — server accepts immediately and fills drafts in the background.
      await post("posts/ai-planner", captionsPayload)

      const captions = { data: [], ok: true, aiPlannerAsync: true }

      const eventMedia = {
        ok: true,
        eventMediaMap: eventMediaMapIds,
        fromGridSelection: true,
      }

      const result = { eventMedia, captions }
      let pipelinePollMerged = 0

      if (sessionContext?.clientId) {
        try {
          const pollResult = await pollPipelinePostsUntilReady(dispatch, {
            clientId: sessionContext.clientId,
            replacePipelinePostsInMatchedGroups: !appendOnly,
            onPollAttempt: sessionContext?.onPipelinePollAttempt,
          })
          pipelinePollMerged =
            pollResult?.hasPipelinePendingRows && Number(pollResult?.merged) > 0
              ? Number(pollResult.merged)
              : 0
          if (pollResult?.timedOut) {
            await dispatch(
              fetchPipelinePostsForAiPlanner({
                clientId: sessionContext.clientId,
                replacePipelinePostsInMatchedGroups: !appendOnly,
              }),
            ).unwrap()
          }
        } catch {
          /* pipeline sync failed — generation still kicked off on server */
        }
      }

      dispatch(completeGeneration())
      if (!isCurrentlyOnAiPlannerRoute()) {
        showAppToast(dispatch, AI_PLANNER_READY_TOAST)
      }

      return { ...result, mappedGroups: [], pipelinePollMerged }
    } catch (err) {
      return rejectWithValue(err?.message || "AI generation failed")
    }
  },
)

/** Convert event-media response to { [eventId]: number[] } (`media_asset_id` when present). */
export function buildEventMediaMapIds(eventMediaResult) {
  if (!eventMediaResult) return {}
  const out = {}
  const direct = eventMediaResult.eventMediaMap
  if (direct && typeof direct === "object") {
    Object.keys(direct).forEach((k) => {
      out[k] = mediaAssetIdsFromEventMediaBucket(direct[k])
    })
  }
  const mappings = eventMediaResult.mappings
  if (Array.isArray(mappings)) {
    mappings.forEach((m) => {
      if (m?.eventId != null && Array.isArray(m.mediaIds)) {
        out[String(m.eventId)] = mediaAssetIdsFromEventMediaBucket(m.mediaIds)
      }
    })
  }
  return out
}

const initialState = {
  eventMediaStatus: "idle",
  captionsStatus: "idle",
  regenerateStatus: "idle",
  generateChainStatus: "idle",
  error: null,
  lastEventMedia: null,
  /** Fingerprint of the last successful `postEventMedia` request body (see {@link fingerprintEventMediaPayload}). */
  lastEventMediaPayloadFingerprint: null,
  lastCaptions: null,
  lastRegenerate: null,
}

const aiPlannerApiSlice = createSlice({
  name: "aiPlannerApi",
  initialState,
  reducers: {
    resetAiPlannerApi: (state) => {
      state.eventMediaStatus = "idle"
      state.captionsStatus = "idle"
      state.regenerateStatus = "idle"
      state.generateChainStatus = "idle"
      state.error = null
      state.lastEventMedia = null
      state.lastEventMediaPayloadFingerprint = null
    },
    clearAiPlannerApiError: (state) => {
      state.error = null
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(postEventMedia.pending, (state) => {
        state.eventMediaStatus = "loading"
        state.error = null
      })
      .addCase(postEventMedia.fulfilled, (state, action) => {
        state.eventMediaStatus = "succeeded"
        state.lastEventMedia = action.payload
        state.lastEventMediaPayloadFingerprint = fingerprintEventMediaPayload(action.meta.arg)
      })
      .addCase(postEventMedia.rejected, (state, action) => {
        state.eventMediaStatus = "failed"
        state.error = action.payload
      })
      .addCase(postEventMediaByIds.pending, (state) => {
        state.eventMediaStatus = "loading"
        state.error = null
      })
      .addCase(postEventMediaByIds.fulfilled, (state, action) => {
        state.eventMediaStatus = "succeeded"
        state.lastEventMedia = action.payload
        state.lastEventMediaPayloadFingerprint = fingerprintEventMediaPayload(action.meta.arg)
      })
      .addCase(postEventMediaByIds.rejected, (state, action) => {
        state.eventMediaStatus = "failed"
        state.error = action.payload
      })
      .addCase(postAiCaptions.pending, (state) => {
        state.captionsStatus = "loading"
        state.error = null
      })
      .addCase(postAiCaptions.fulfilled, (state, action) => {
        state.captionsStatus = "succeeded"
        state.lastCaptions = action.payload
      })
      .addCase(postAiCaptions.rejected, (state, action) => {
        state.captionsStatus = "failed"
        state.error = action.payload
      })
      .addCase(postRegenerateAiPosts.pending, (state) => {
        state.regenerateStatus = "loading"
        state.error = null
      })
      .addCase(postRegenerateAiPosts.fulfilled, (state, action) => {
        state.regenerateStatus = "succeeded"
        state.lastRegenerate = action.payload
      })
      .addCase(postRegenerateAiPosts.rejected, (state, action) => {
        state.regenerateStatus = "failed"
        state.error = action.payload
      })
      .addCase(generateAiPlannerPosts.pending, (state) => {
        state.generateChainStatus = "loading"
        state.error = null
      })
      .addCase(generateAiPlannerPosts.fulfilled, (state, action) => {
        state.generateChainStatus = "succeeded"
        state.lastEventMedia = action.payload.eventMedia
        state.lastCaptions = action.payload.captions
        const emPayload = action.meta?.arg?.eventMediaPayload
        state.lastEventMediaPayloadFingerprint = fingerprintEventMediaPayload(emPayload)
      })
      .addCase(generateAiPlannerPosts.rejected, (state, action) => {
        state.generateChainStatus = "failed"
        state.error = action.payload
      })
  },
})

export const { resetAiPlannerApi, clearAiPlannerApiError } = aiPlannerApiSlice.actions
export default aiPlannerApiSlice.reducer
